import React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Box, flameTheme, Flex } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { Text, TextLink } from '@lightspeed/flame/Text';

import StyledCountdownCalendar from 'pages/views/reports/CustomerData/BannerOffendingRecordsDetected/components/StyledCountdownCalendar';
import {
  CUSTOMER_LIGHTSPEED_PAYMENTS_LIGHTSPEED_ARTICLE,
  CUSTOMER_SAVED_PAYMENTS_LIGHTSPEED_ARTICLE,
  numDaysBetween,
  useGatewayNameQuery,
} from 'pages/views/reports/CustomerData/utils';
import { trackAnalyticsEvent } from 'root/apps/helpers/AnalyticsTracking';
import useLocalizedLink from 'root/apps/hooks/useLocalizedLink';
import OptInModal from 'root/apps/SavedCards/OptInModal';
import Modules from 'root/apps/shared_modules/Modules';

// Status constants for customer acknowledgment
const CUSTOMER_ACKNOWLEDGMENT_STATUSES = {
  ACKNOWLEDGED: '1',
  NOT_ACKNOWLEDGED: '0',
} as const;

const BannerOffendingRecordsDetected = ({
  offendingRecordsCount = 0,
}: {
  offendingRecordsCount: number;
}) => {
  const [dateSensitiveInformationRemoved, setDateSensitiveInformationRemoved] = React.useState<
    string | null
  >(null);
  const [daysFromNow, setDaysFromNow] = React.useState<number | null>(null);
  const [isOptInModalOpen, setIsOptInModalOpen] = React.useState<boolean>(false);
  const [customerAcknowledgedCardOnFile, setCustomerAcknowledgedCardOnFile] =
    React.useState<boolean>(true);
  const [isCheckingAcknowledgment, setIsCheckingAcknowledgment] = React.useState<boolean>(true);

  const { colors } = flameTheme;

  const panEventLabel = customerAcknowledgedCardOnFile ? 'Acknowledged' : 'Unacknowledged';

  // Use the localized link hook to get the correct URL based on user's locale
  const localizedArticleUrl = useLocalizedLink(CUSTOMER_SAVED_PAYMENTS_LIGHTSPEED_ARTICLE);

  // https://www.lightspeedhq.com/ecom/lightspeed-payments/
  const localizedLSPayArticleUrl = useLocalizedLink(
    CUSTOMER_LIGHTSPEED_PAYMENTS_LIGHTSPEED_ARTICLE
  );

  const dateFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  } as Intl.DateTimeFormatOptions;

  const gatewayNameQuery = useGatewayNameQuery();

  const isGatewayLightspeedPayments = gatewayNameQuery?.data === 'lightspeed-payments';

  React.useEffect(() => {
    const getOffendingRecordsCleanDate = async () => {
      const cleanOffendingRecordsConfigDate = await window.lsCloud.Config.access(
        'offending_records_clean_date'
      );

      // Create the target date and ensure it's treated as local time
      const targetDate = new Date(`${cleanOffendingRecordsConfigDate}T00:00:00`);

      setDateSensitiveInformationRemoved(
        Intl.DateTimeFormat(navigator.language, dateFormatOptions).format(targetDate)
      );

      const now = new Date();
      now.setHours(0, 0, 0, 0);

      setDaysFromNow(numDaysBetween(now, targetDate));
    };

    const checkCardOnFileAcknowledgment = async () => {
      try {
        const acknowledged = await window.lsCloud.Options.get('customer_use_card_on_file');
        setCustomerAcknowledgedCardOnFile(
          acknowledged === CUSTOMER_ACKNOWLEDGMENT_STATUSES.ACKNOWLEDGED
        );
      } catch (error) {
        setCustomerAcknowledgedCardOnFile(false);
      } finally {
        setIsCheckingAcknowledgment(false);
      }
    };

    getOffendingRecordsCleanDate();
    checkCardOnFileAcknowledgment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Flex padding="20px" alignItems="center" height="100%">
        <Flex flexDirection="column" flexWrap="wrap" width="70%" pr="1rem">
          <Box>
            <Text
              fontSize="21px"
              fontWeight="bold"
              mb={2}
              data-test="banner-title-offending-records-detected"
            >
              Action required: Store credit card details securely{' '}
              <span style={{ marginLeft: '4px' }} role="img" aria-label="Credit Card">
                &#128179;
              </span>
            </Text>
            <Text
              as="div"
              fontSize="14px"
              mb={3}
              color={colors.textDimmed}
              data-test="banner-message-offending-records-detected"
            >
              <span>
                We&rsquo;ve noticed sensitive information, including card information, currently
                stored within your customer detail pages. Please be advised that by{' '}
                <strong>
                  <var style={{ whiteSpace: 'nowrap' }}>{dateSensitiveInformationRemoved}</var>
                </strong>
                , all numeric information from the table will be removed. If there is any
                non-sensitive information you need to retain, please report it. This ensures you
                will not lose it as part of this cleanup.
              </span>
              {!isCheckingAcknowledgment &&
                !gatewayNameQuery.isLoading &&
                !customerAcknowledgedCardOnFile &&
                isGatewayLightspeedPayments && (
                  <>
                    <Divider mt={3} mb={3} />
                    <span>
                      To help you easily and securely store customer card information, we recently
                      launched Saved Payment Method within Lightspeed Payments. Enable the feature
                      and navigate to your customer&rsquo;s profile to start securely storing
                      customer information.{' '}
                      <TextLink
                        href={localizedLSPayArticleUrl}
                        target="_blank"
                        onClick={() => {
                          if (Modules.hasModule('data.pan_detection.tracker')) {
                            trackAnalyticsEvent({
                              eventCategory: 'PAN',
                              eventName: 'Report',
                              eventAction: 'LearnLSPayViaHeaderBanner',
                              eventLabel: panEventLabel,
                              eventValue: offendingRecordsCount,
                            });
                          }
                        }}
                      >
                        Learn more
                      </TextLink>
                    </span>
                  </>
                )}
              {isGatewayLightspeedPayments && (
                <p>
                  Helpful Tip: To help ensure smooth future checkouts, please make sure you are
                  adding customers’ latest card information.
                </p>
              )}
              {!isCheckingAcknowledgment &&
                !gatewayNameQuery.isLoading &&
                !isGatewayLightspeedPayments && (
                  <>
                    <Divider mt={3} mb={3} />
                    <span>
                      Good news! To help you easily and securely store customer card information, we
                      recently launched Saved Payment Method within{' '}
                      <TextLink
                        href={localizedArticleUrl}
                        target="_blank"
                        onClick={() => {
                          if (Modules.hasModule('data.pan_detection.tracker')) {
                            trackAnalyticsEvent({
                              eventCategory: 'PAN',
                              eventName: 'Report',
                              eventAction: 'LearnViaHeaderBanner',
                              eventLabel: panEventLabel,
                              eventValue: offendingRecordsCount,
                            });
                          }
                        }}
                      >
                        Lightspeed Payments.
                      </TextLink>
                      <br />
                      Sign up for Lightspeed Payments to easily and securely store customer credit
                      card details and more:
                      <ul>
                        <li>Simplified operations with a fully integrated system</li>
                        <li>Clearer financial insights, all in one place</li>
                        <li>One support team for everything—no more third parties</li>
                      </ul>
                    </span>
                  </>
                )}
            </Text>
            {!isCheckingAcknowledgment &&
              !gatewayNameQuery.isLoading &&
              !customerAcknowledgedCardOnFile &&
              isGatewayLightspeedPayments && (
                <Button
                  onClick={() => {
                    if (Modules.hasModule('data.pan_detection.tracker')) {
                      trackAnalyticsEvent({
                        eventCategory: 'PAN',
                        eventName: 'Report',
                        eventAction: 'EnableViaHeaderBanner',
                        eventLabel: panEventLabel,
                        eventValue: offendingRecordsCount,
                      });
                    }
                    setIsOptInModalOpen(true);
                  }}
                  data-test="banner-button-enable-saved-payment-method"
                >
                  Enable Saved Payment Method
                </Button>
              )}
            {!isCheckingAcknowledgment &&
              !gatewayNameQuery.isLoading &&
              !isGatewayLightspeedPayments && (
                <Button
                  onClick={() => {
                    if (Modules.hasModule('data.pan_detection.tracker')) {
                      trackAnalyticsEvent({
                        eventCategory: 'PAN',
                        eventName: 'Report',
                        eventAction: 'EnableLSPayViaHeaderBanner',
                        eventLabel: 'ViewFinancialServices',
                      });
                    }
                    window.PageRouter.goTo('/payments/lightspeed-payments/merchant-portal');
                  }}
                  data-test="banner-button-enable-saved-payment-method-reports"
                  variant="secondary"
                  fill={true}
                >
                  Sign up to Lightspeed Payments
                </Button>
              )}
          </Box>
        </Flex>
        <Flex paddingRight="39px" width="30%" justifyContent="right">
          <StyledCountdownCalendar daysFromNow={daysFromNow} />
        </Flex>
      </Flex>

      <OptInModal
        isOpen={isOptInModalOpen}
        onRequestClose={() => setIsOptInModalOpen(false)}
        onComplete={() => {
          setCustomerAcknowledgedCardOnFile(true);
        }}
        swapStepTwoButtonOrder={true}
        isFromReportPage={true}
      />
    </>
  );
};

export default BannerOffendingRecordsDetected;
